import { Injectable } from '@angular/core';
import { ITextWhithChildrenItem } from "libraries/component/booking-information/components/text-with-children/text-with-children.component";
import { NotificationType } from "libraries/interfaces/INotification";
import * as moment from 'moment';
import { ErrorTracingService, TraceSeverity } from "libraries/service/error-tracing/error-tracing.service";
import { IServiceOperation, IBookingEligibility, TGetBookingResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { OperationStatus } from 'libraries/service/resources/model/search/IBooking';


export interface IOperationItem {
    dateTime: Date;
    serviceTitle: string;
    serviceCode: string;
    result: OperationStatus;
    messages: ITextWhithChildrenItem[];
}

@Injectable()
export class BookingOperationService {

    private statusCache: string[] = [];

    constructor(
        private readonly errorTracingService: ErrorTracingService,
    ) {
    }

    public getOperationsStatus(operations: IServiceOperation[]): OperationStatus {
        if (!operations.length) {
            return OperationStatus.success;
        }
        return operations
            .map((svcOper: IServiceOperation) => {
                return this.getStatusFromString(svcOper.name);
            })
            .reduce((prevSvcOper: OperationStatus, curSvcOper: OperationStatus): OperationStatus => {
                return isWorstThan(curSvcOper, prevSvcOper) ? curSvcOper : prevSvcOper;
            }, OperationStatus.success);
    }

    public getOperationItems(operations: IServiceOperation[]): ITextWhithChildrenItem[] {
        return operations.map((svcOper: IServiceOperation): ITextWhithChildrenItem => {
            return {
                text: svcOper.title,
                children: svcOper.messages.map((msg: string) => {
                    return { text: msg };
                })
            };
        });
    }

    public getNotificationType(status: OperationStatus): NotificationType {
        switch (status) {
            case OperationStatus.success:
                return NotificationType.Success;
            case OperationStatus.warning:
                return NotificationType.Warning;
            case OperationStatus.error:
                return NotificationType.Error;
            default:
                if(!this.statusCache.includes(status)){
                    this.statusCache.push(status);
                    // this.errorTracingService.captureMessage(`Unknown IOperationStatus '${status}'`, TraceSeverity.Warning);
                    console.warn(`Unknown IOperationStatus '${status}'`);
                }
                return NotificationType.Error;
        }
    }

    public isEligible(curElig: IBookingEligibility): boolean {
        let eligible: boolean = false;
        if (curElig.status === 'Applicable') {

            const now: moment.Moment = moment();
            const yesterday: moment.Moment = moment().subtract(1, 'day');

            const details: { type?: string; event?: string; start?: string; end?: string } = curElig.details;
            const detailsType: string = curElig.details.type;

            if (detailsType === null || detailsType === 'NOW' || detailsType === 'EVENT') {
                eligible = true;
            } else if (detailsType === 'DATE_RANGE'
                && moment(details.start) < now
                && moment(details.end) > yesterday) {
                eligible = true;
            }
        }
        return eligible;
    }

    private getStatusFromString(value: string): OperationStatus {
        switch (value.split('.').pop()) {
            case 'SUCCESS':
                return OperationStatus.success;
            case 'ALERT':
                return OperationStatus.warning;
            case 'FAILED':
                return OperationStatus.error;
            default:
                if(!this.statusCache.includes(value)){
                    this.statusCache.push(value);
                    // this.errorTracingService.captureMessage(`Unknown IOperationStatus '${status}'`, TraceSeverity.Warning);
                    console.warn(`Unknown status (from operation code) '${value}'`, TraceSeverity.Warning);
                }
                // this.errorTracingService.captureMessage(`Unknown status (from operation code) '${value}'`, TraceSeverity.Warning);
                return OperationStatus.error;
        }
    }
}

function isWorstThan(statusA: OperationStatus, statusB: OperationStatus): boolean {
    const statusToNumMap: { [key in keyof typeof OperationStatus]: number } = {
        success: 1,
        warning: 2,
        error: 3,
    };

    const numA: number = statusToNumMap[statusA];
    const numB: number = statusToNumMap[statusB];

    return numA > numB;
}
